/**
 * WindowManager - Sistema de ventanas flotantes
 * Gestiona múltiples ventanas abiertas simultáneamente
 * Todas las secciones del dashboard como ventanas independientes
 */
import React, { useState, useCallback, useEffect, lazy, Suspense, useMemo, useRef } from 'react';
import { logger } from '../utils/logger';
import FloatingWindow, { WIN_ACCENT_HEX } from './FloatingWindow';
import { 
  Calculator, Lightning, TrendUp, ArrowsClockwise, Buildings,
  CurrencyCircleDollar, Database, Scales, CalendarBlank, ChartPieSlice,
  ChartLine, Briefcase, Newspaper, Bell, Monitor, GitDiff, MagnifyingGlass,
  ChartBar, Globe, Table, GridFour, Book, ClockCounterClockwise, Gauge,
  Crosshair, Sparkle, Fire
} from '@phosphor-icons/react';

// Lazy load de componentes
const PerformanceAnalysis = lazy(() => import('./PerformanceAnalysis'));
const TradingSignals = lazy(() => import('./TradingSignals'));
const CategoryPanel = lazy(() => import('./CategoryPanel'));
const AdvancedBacktesting = lazy(() => import('./AdvancedBacktesting'));
const SectorAnalysis = lazy(() => import('./SectorAnalysis'));
const PortfolioSummary = lazy(() => import('./PortfolioSummary'));
const MarketNews = lazy(() => import('./MarketNews'));
const AlertsPanel = lazy(() => import('./AlertsPanel'));
const BigScreenMode = lazy(() => import('./BigScreenMode'));
const MultiAssetComparator = lazy(() => import('./MultiAssetComparator'));
// FUSIONADO: IndexCryptoComparator ahora es parte de UniversalComparator
const UniversalComparator = lazy(() => import('./UniversalComparator'));
// FUSIONADOS: Componentes center
const HeatmapCenter = lazy(() => import('./HeatmapCenter'));
const HistoricalCenter = lazy(() => import('./HistoricalCenter'));
const CalculatorCenter = lazy(() => import('./CalculatorCenter'));
const AssetAnalyzer = lazy(() => import('./AssetAnalyzer'));
const BuyStrategy = lazy(() => import('./BuyStrategy'));
const LivePortfolio = lazy(() => import('./LivePortfolio'));
const PortfolioOverview = lazy(() => import('./PortfolioOverview'));
const PortfolioEvolutionHistory = lazy(() => import('./PortfolioEvolutionHistory'));
// Nuevos componentes
const HistorialMaxMin = lazy(() => import('./HistorialMaxMin'));
const CorrelationListView = lazy(() => import('./CorrelationListView'));
const CorrelacionesContent = lazy(() => import('./CorrelacionesContent'));
const CandlestickChartWindow = lazy(() => import('./CandlestickChartWindow'));
const FinvizMap = lazy(() => import('./FinvizMap'));
const BackupManager = lazy(() => import('./BackupManager'));
const MarketDatabase = lazy(() => import('./MarketDatabase'));
const MultiChartView = lazy(() => import('./MultiChartView'));
const FinancialDatabase = lazy(() => import('./FinancialDatabase'));
const Contabilidad = lazy(() => import('./Contabilidad'));
const HistorialContable = lazy(() => import('./HistorialContable'));
const StockScreenerPro = lazy(() => import('./StockScreenerPro'));
const QualityTrendScreener = lazy(() => import('./QualityTrendScreener'));
// FUSIONADO: StockComparatorPro ahora es parte de UniversalComparator
// REEMPLAZADO: FundamentalAnalysis sustituido por ProAnalyst (Ultimate)
const IndicadoresTecnicosPanel = lazy(() => import('./IndicadoresTecnicosPanel'));
const SectorExplorer = lazy(() => import('./SectorExplorer'));
const EconomicCalendarPro = lazy(() => import('./EconomicCalendarPro'));
const DividendTrackerPro = lazy(() => import('./DividendsCenter'));
const FinancialGoals = lazy(() => import('./FinancialGoals'));
const EBITDAAnalysis = lazy(() => import('./EBITDAAnalysis'));
const ProAnalyst = lazy(() => import('./ProAnalyst'));
const Asset360Report = lazy(() => import('./Asset360Report'));

// Componente simplificado de Multi-Gráfico (sin portal)
const MultiChartViewContent = lazy(() => import('./MultiChartContent'));
const UserManual = lazy(() => import('./UserManual'));
const Watchlists = lazy(() => import('./Watchlists'));
const CandlestickPatterns = lazy(() => import('./CandlestickPatterns'));
const SentimentComparator = lazy(() => import('./SentimentComparator'));
const EmailAlerts = lazy(() => import('./EmailAlerts'));
const TradingJournal = lazy(() => import('./TradingJournal'));
const PaperTrading = lazy(() => import('./PaperTrading'));
const GapHistory = lazy(() => import('./GapHistory'));

// Configuración de todas las ventanas disponibles
// requiredPlan: 'free' (todos), 'basic' (básico+premium), 'premium' (solo premium)
export const WINDOWS_CONFIG = [
  // ANÁLISIS - Disponibles para BÁSICO+
  {
    id: 'asset360',
    name: 'Análisis Bursátil 360',
    fullName: 'Análisis Bursátil 360 del Activo',
    description: 'Todo sobre un activo: valoración, salud, técnico, noticias, veredicto IA y web',
    icon: Sparkle,
    color: '#E8C55C',
    component: 'Asset360Report',
    defaultSize: { width: 1240, height: 880 },
    requiredPlan: 'basic'
  },
  {
    id: 'indicadoresPro',
    name: 'Análisis Técnico Profesional',
    fullName: 'Análisis Técnico Profesional',
    description: 'RSI, MACD, Bollinger, ADX, Fibonacci, ATR',
    icon: Gauge,
    color: '#06b6d4',
    component: 'IndicadoresTecnicosPanel',
    defaultSize: { width: 600, height: 800 },
    requiredPlan: 'basic'
  },
  {
    id: 'gapHistory',
    name: 'Historial de Gaps',
    fullName: 'Historial de Gaps (huecos de apertura)',
    description: 'Huecos de apertura detectados en tus activos',
    icon: TrendUp,
    color: '#E8C55C',
    component: 'GapHistory',
    defaultSize: { width: 720, height: 720 },
    requiredPlan: 'free'
  },
  {
    id: 'rendimiento',
    name: 'Análisis de Rendimiento',
    fullName: 'Análisis de Rendimiento',
    description: 'Métricas y estadísticas',
    icon: TrendUp,
    color: '#00ff6a',
    component: 'PerformanceAnalysis',
    defaultSize: { width: 700, height: 550 },
    requiredPlan: 'free'
  },
  {
    id: 'signals',
    name: 'Análisis Señales Trading',
    fullName: 'Análisis Señales Trading',
    description: 'Señales técnicas automáticas',
    icon: Lightning,
    color: '#ff9500',
    component: 'TradingSignals',
    defaultSize: { width: 700, height: 600 },
    requiredPlan: 'basic'
  },
  {
    id: 'buystrategy',
    name: 'Estrategia',
    fullName: 'Estrategia de Compra',
    description: 'DCA, rebalanceo y objetivos',
    icon: Briefcase,
    color: '#10b981',
    component: 'BuyStrategy',
    defaultSize: { width: 550, height: 650 },
    requiredPlan: 'basic'
  },
  
  // CONTEXTO - BÁSICO+
  {
    id: 'categories',
    name: 'Análisis de Sectores',
    fullName: 'Análisis de Sectores',
    description: 'Exploración de sectores, categorías y rendimiento',
    icon: ChartPieSlice,
    color: '#06b6d4',
    component: 'SectorExplorer',  // Nuevo componente unificado
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'economicCalendar',
    name: 'Calendario Económico',
    fullName: 'Calendario Económico',
    description: 'Eventos que mueven los mercados',
    icon: CalendarBlank,
    color: '#f59e0b',
    component: 'EconomicCalendarPro',
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'dividendTracker',
    name: 'Centro de Dividendos',
    fullName: 'Centro de Dividendos',
    description: 'Lo que cobras y cobrarás por tener tus activos',
    icon: CurrencyCircleDollar,
    color: '#10b981',
    component: 'DividendTrackerPro',
    defaultSize: { width: 1100, height: 780 },
    requiredPlan: 'basic'
  },
  
  // HERRAMIENTAS PRO - PREMIUM
  {
    id: 'backtesting',
    name: 'Backtesting',
    fullName: 'Backtesting Avanzado',
    description: 'Simula estrategias pasadas',
    icon: Database,
    color: '#3b82f6',
    component: 'AdvancedBacktesting',
    defaultSize: { width: 850, height: 650 },
    requiredPlan: 'premium'
  },
  
  // GESTIÓN
  {
    id: 'portfolio',
    name: 'Portafolio',
    fullName: 'Mi Portafolio',
    description: 'Tus inversiones y seguimiento',
    icon: Briefcase,
    color: '#a855f7',
    component: 'PortfolioSummary',
    defaultSize: { width: 920, height: 880 },
    requiredPlan: 'basic'
  },
  {
    id: 'portfolioLista',
    name: 'Cartera Lista',
    fullName: 'Cartera — Lista Detallada',
    description: 'Lista completa de tu cartera',
    icon: Briefcase,
    color: '#e8c55c',
    component: 'PortfolioListWindow',
    defaultSize: { width: 1180, height: 760 },
    requiredPlan: 'basic',
    hidden: true
  },
  {
    id: 'portfolioGrafico',
    name: 'Cartera Gráfico',
    fullName: 'Cartera — Distribución',
    description: 'Gráfico de distribución de tu cartera',
    icon: TrendUp,
    color: '#e8c55c',
    component: 'PortfolioChartWindow',
    defaultSize: { width: 1040, height: 720 },
    requiredPlan: 'basic',
    hidden: true
  },
  {
    id: 'portfolioEvolucion',
    name: 'Evolución Cartera',
    fullName: 'Evolución de mi Cartera',
    description: 'Gráfico histórico del valor de tu cartera',
    icon: TrendUp,
    color: '#e8c55c',
    component: 'PortfolioEvolutionHistory',
    defaultSize: { width: 1100, height: 720 },
    requiredPlan: 'basic'
  },
  {
    id: 'livePortfolio',
    name: 'Cartera Vivo',
    fullName: 'Cartera en Vivo',
    description: 'Valor en tiempo real',
    icon: TrendUp,
    color: '#00ff6a',
    component: 'LivePortfolio',
    defaultSize: { width: 1100, height: 750 },
    requiredPlan: 'basic'
  },
  {
    id: 'contabilidad',
    name: 'Contabilidad',
    fullName: 'Contabilidad',
    description: 'Registro y análisis de beneficios',
    icon: Calculator,
    color: '#10b981',
    component: 'Contabilidad',
    defaultSize: { width: 800, height: 700 },
    requiredPlan: 'premium'  // Ultimate en la práctica (verificación en backend)
  },
  {
    id: 'proAnalyst',
    name: 'Analista Pro',
    fullName: 'Analista Pro (KAPITAL Pro Analyst)',
    description: 'Fair Value + Health Score + Altman Z + ProTips IA',
    icon: Sparkle,
    color: '#ffd700',
    component: 'ProAnalyst',
    defaultSize: { width: 1200, height: 850 },
    requiredPlan: 'ultimate'
  },
  {
    id: 'ebitdaAnalysis',
    name: 'Análisis Margen EBITDA',
    fullName: 'Análisis Margen EBITDA',
    description: 'Métricas fundamentales con énfasis en márgenes EBITDA',
    icon: ChartBar,
    color: '#0ea5e9',
    component: 'EBITDAAnalysis',
    defaultSize: { width: 1100, height: 800 },
    requiredPlan: 'premium'  // Ultimate - verificación en backend
  },
  {
    id: 'historialContable',
    name: 'Historial',
    fullName: 'Historial Contable',
    description: 'Desglose por días, semanas, meses y años',
    icon: ClockCounterClockwise,
    color: '#a855f7',
    component: 'HistorialContable',
    defaultSize: { width: 400, height: 600 },
    requiredPlan: 'premium'
  },
  {
    id: 'marketDatabase',
    name: 'Base Datos',
    fullName: 'Base de Datos de Mercado',
    description: 'Tabla Excel con datos en tiempo real',
    icon: Table,
    color: '#06b6d4',
    component: 'MarketDatabase',
    defaultSize: { width: 1100, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'financialDatabase',
    name: 'Datos Financieros',
    fullName: 'Datos Financieros',
    description: 'Estados financieros, ratios, análisis',
    icon: Scales,
    color: '#a855f7',
    component: 'FinancialDatabase',
    defaultSize: { width: 900, height: 750 },
    requiredPlan: 'ultimate'
  },
  {
    id: 'news',
    name: 'Noticias',
    fullName: 'Noticias del Mercado',
    description: 'Últimas noticias financieras',
    icon: Newspaper,
    color: '#00dcff',
    component: 'MarketNews',
    defaultSize: { width: 850, height: 650 },
    requiredPlan: 'free'
  },
  {
    id: 'alerts',
    name: 'Alertas',
    fullName: 'Centro de Alertas',
    description: 'Gestiona tus alertas de precio',
    icon: Bell,
    color: '#ff6b6b',
    component: 'AlertsPanel',
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'free'
  },
  
  // ESPECIALES
  {
    id: 'analyzer',
    name: 'Analizador',
    fullName: 'Analizador de Activos',
    description: 'Análisis completo con historial',
    icon: MagnifyingGlass,
    color: '#f59e0b',
    component: 'AssetAnalyzer',
    defaultSize: { width: 650, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'universalComparator',
    name: 'Comparador de Activos',
    fullName: 'Comparador de Activos',
    description: 'Compara acciones, índices y criptomonedas en una sola herramienta',
    icon: Scales,
    color: '#a855f7',
    component: 'UniversalComparator',
    defaultSize: { width: 1400, height: 860 },
    requiredPlan: 'ultimate'
  },
  {
    id: 'fullscreen',
    name: 'Cartera Detallada',
    fullName: 'Cartera Detallada',
    description: 'Vista de presentación',
    icon: Monitor,
    color: '#ffd700',
    component: 'BigScreenMode',
    defaultSize: { width: 1400, height: 850 },
    // isFullscreen eliminado (feb 2026): ahora se comporta como FloatingWindow
    // redimensionable/arrastrable como el resto de herramientas. El usuario
    // puede pulsar el botón interno "Pantalla completa" (F11) para maximizar.
    requiredPlan: 'basic'
  },
  
  // NUEVAS HERRAMIENTAS - PREMIUM
  // NOTA: Herramientas fusionadas
  {
    id: 'heatmapCenter',
    name: 'Centro de Heatmaps',
    fullName: 'Centro de Heatmaps',
    description: 'Correlación + Cartera + Mercado Finviz en una sola herramienta',
    icon: ChartPieSlice,
    color: '#a855f7',
    component: 'HeatmapCenter',
    defaultSize: { width: 1100, height: 800 },
    requiredPlan: 'ultimate'
  },
  {
    id: 'historicalCenter',
    name: 'Centro de Históricos',
    fullName: 'Centro de Datos Históricos',
    description: 'OHLCV + Archivo + Máx/Mín en una sola herramienta',
    icon: Database,
    color: '#8b5cf6',
    component: 'HistoricalCenter',
    defaultSize: { width: 1100, height: 800 },
    requiredPlan: 'basic'
  },
  {
    id: 'calculatorCenter',
    name: 'Calculadoras PRO',
    fullName: 'Calculadoras PRO',
    description: 'Riesgo, posiciones y simulador de inversiones',
    icon: Calculator,
    color: '#00dcff',
    component: 'CalculatorCenter',
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'candlestickChart',
    name: 'Mapa Velas Ampliado',
    fullName: 'Mapa Velas Ampliado',
    description: 'Gráfico de velas redimensionable con impresión',
    icon: ChartBar,
    color: '#00ff6a',
    component: 'CandlestickChartWindow',
    defaultSize: { width: 900, height: 820 },
    requiredPlan: 'premium'
  },
  {
    id: 'backup',
    name: 'Backup',
    fullName: 'Gestor de Backups',
    description: 'Gestiona copias de seguridad de tus inversiones',
    icon: Database,
    color: '#22c55e',
    component: 'BackupManager',
    defaultSize: { width: 600, height: 550 },
    requiredPlan: 'basic'
  },
  {
    id: 'multiChart',
    name: 'Multi-Gráfico',
    fullName: 'Multi-Gráfico',
    description: 'Analiza 2, 4 o 6 activos simultáneamente',
    icon: GridFour,
    color: '#00dcff',
    component: 'MultiChartView',
    defaultSize: { width: 1400, height: 800 },
    fullscreen: true,
    requiredPlan: 'premium'
  },
  {
    id: 'manual',
    name: 'Manual',
    fullName: 'Manual de Usuario',
    description: 'Guía paso a paso para usar KAPITAL',
    icon: Book,
    color: '#22d3ee',
    component: 'UserManual',
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'free'
  },
  {
    id: 'stockScreener',
    name: 'Buscador',
    fullName: 'Buscador de Acciones',
    description: 'Encuentra acciones con filtros avanzados',
    icon: MagnifyingGlass,
    color: '#06b6d4',
    component: 'StockScreenerPro',
    defaultSize: { width: 1200, height: 750 },
    requiredPlan: 'free'
  },
  {
    id: 'qualityTrend',
    name: 'Calidad + Tendencia',
    fullName: 'Buscador Calidad + Tendencia',
    description: 'Empresas rentables en tendencia alcista, enlazado al mapa de calor',
    icon: Fire,
    color: '#f59e0b',
    component: 'QualityTrendScreener',
    defaultSize: { width: 1150, height: 720 },
    requiredPlan: 'free'
  },
  // NOTA: stockComparator ELIMINADO - fusionado en 'universalComparator'
  {
    id: 'financialGoals',
    name: 'Objetivos Financieros',
    fullName: 'Objetivos Financieros',
    description: 'Define y alcanza tus metas de ahorro e inversión',
    icon: TrendUp,
    color: '#a855f7',
    component: 'FinancialGoals',
    defaultSize: { width: 1100, height: 750 },
    requiredPlan: 'premium'
  },
  {
    id: 'watchlists',
    name: 'Listas de Seguimiento',
    fullName: 'Listas de Seguimiento',
    description: 'Watchlists personalizadas con precios en vivo',
    icon: Crosshair,
    color: '#f59e0b',
    component: 'Watchlists',
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'candlestickPatterns',
    name: 'Patrones de Velas',
    fullName: 'Detector de Patrones de Velas',
    description: 'Hammer, Engulfing, Doji y más',
    icon: ChartBar,
    color: '#22c55e',
    component: 'CandlestickPatterns',
    defaultSize: { width: 600, height: 500 },
    requiredPlan: 'basic'
  },
  {
    id: 'sentimentComparator',
    name: 'Comparador Sentimiento',
    fullName: 'Comparador de Sentimiento Multi-Ticker',
    description: 'Superpone hasta 6 tickers y detecta divergencias',
    icon: ChartBar,
    color: '#22d3ee',
    component: 'SentimentComparator',
    defaultSize: { width: 820, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'emailAlerts',
    name: 'Alertas Email',
    fullName: 'Alertas por Email',
    description: 'Recibe alertas sin tener la app abierta',
    icon: Bell,
    color: '#ec4899',
    component: 'EmailAlerts',
    defaultSize: { width: 600, height: 600 },
    requiredPlan: 'premium'
  },
  {
    id: 'tradingJournal',
    name: 'Diario Trading',
    fullName: 'Diario de Trading',
    description: 'Registra y analiza tus operaciones',
    icon: Book,
    color: '#f97316',
    component: 'TradingJournal',
    defaultSize: { width: 800, height: 700 },
    requiredPlan: 'basic'
  },
  {
    id: 'paperTrading',
    name: 'Paper Trading',
    fullName: 'Cartera Simulada (Paper Trading)',
    description: 'Practica trading sin dinero real',
    icon: Briefcase,
    color: '#14b8a6',
    component: 'PaperTrading',
    defaultSize: { width: 900, height: 700 },
    requiredPlan: 'basic'
  },
];

// Componente de carga
const LoadingSpinner = ({ color }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-3">
    <div 
      className="animate-spin rounded-full h-10 w-10 border-3"
      style={{ 
        borderColor: `${color}30`,
        borderTopColor: color
      }}
    />
    <span className="text-sm text-gray-400">Cargando...</span>
  </div>
);

// Renderizar contenido de ventana con opciones
const WindowContent = ({ windowId, ticker, stockHistory, stockInfo, onTickerSelect, portfolioTickers, enrichedMarkets = [], options = {}, onClose, onMinimize, openWindow, onFocus }) => {
  // Parse investments con manejo de errores
  let investments = {};
  try {
    const savedInv = localStorage.getItem('myInvestments');
    if (savedInv) investments = JSON.parse(savedInv);
  } catch (e) {
    console.error('Error parsing myInvestments:', e);
    localStorage.removeItem('myInvestments');
  }
  
  // Obtener plan del usuario desde múltiples fuentes
  const getUserPlan = () => {
    const directPlan = localStorage.getItem('kapital_user_plan');
    if (directPlan) return directPlan;
    
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        return parsed.plan || 'free';
      } catch (e) {
        return 'free';
      }
    }
    return 'free';
  };
  const userPlan = getUserPlan();
  
  // Estado para modo presentación - sincronizado con localStorage y eventos
  const [presentationMode, setPresentationMode] = useState(() => {
    return localStorage.getItem('kapital_presentation_mode') === 'true';
  });
  
  // Escuchar cambios en el modo presentación desde la pantalla principal
  useEffect(() => {
    const handlePresentationModeChange = (e) => {
      setPresentationMode(e.detail.enabled);
    };
    window.addEventListener('presentationModeChanged', handlePresentationModeChange);
    
    // También verificar localStorage por si el evento se pierde
    const checkStorage = () => {
      const stored = localStorage.getItem('kapital_presentation_mode') === 'true';
      if (stored !== presentationMode) {
        setPresentationMode(stored);
      }
    };
    const interval = setInterval(checkStorage, 1000);
    
    return () => {
      window.removeEventListener('presentationModeChanged', handlePresentationModeChange);
      clearInterval(interval);
    };
  }, [presentationMode]);
  
  // Memoizar objetos inline para evitar re-renders innecesarios
  const emptyPrices = useMemo(() => ({}), []);
  const emptyArray = useMemo(() => [], []);
  const initialTickers = useMemo(() => ticker ? [ticker] : [], [ticker]);
  
  // Log para debug de opciones seleccionadas
  if (options && options.feature) {
    logger.log(`Window ${windowId} opened with feature: ${options.feature}`);
  }
  
  switch (windowId) {
    case 'indicadoresPro':
      return <IndicadoresTecnicosPanel initialTicker={options?.ticker || ticker} />;
    case 'rendimiento':
      return <PerformanceAnalysis initialTicker={ticker} onTickerChange={onTickerSelect} />;
    case 'signals':
      return <TradingSignals ticker={ticker} stockHistory={stockHistory} stockInfo={stockInfo} />;
    case 'buystrategy':
      return <BuyStrategy investments={investments} markets={enrichedMarkets} presentationMode={presentationMode} />;
    case 'categories':
      return <SectorExplorer onSelectTicker={onTickerSelect} />;
    case 'economicCalendar':
      return <EconomicCalendarPro onClose={onClose} />;
    case 'dividendTracker':
      return <DividendTrackerPro onSelectTicker={onTickerSelect} onClose={onClose} />;
    case 'backtesting':
      return <AdvancedBacktesting ticker={ticker} stockHistory={stockHistory} />;
    case 'portfolio':
      return <PortfolioSummary onTickerSelect={onTickerSelect} presentationMode={presentationMode} openWindow={openWindow} />;
    case 'portfolioLista':
      return <PortfolioOverview mode="lista" onTickerSelect={onTickerSelect} presentationMode={presentationMode} isFullscreenMode={true} showFullscreen={false} />;
    case 'portfolioGrafico':
      return <PortfolioOverview mode="grafico" onTickerSelect={onTickerSelect} presentationMode={presentationMode} isFullscreenMode={true} showFullscreen={false} />;
    case 'portfolioEvolucion':
      return <PortfolioEvolutionHistory presentationMode={presentationMode} initialTab={options?.tab} />;
    case 'livePortfolio':
      return <LivePortfolio onTickerSelect={onTickerSelect} presentationMode={presentationMode} openWindow={openWindow} />;
    case 'contabilidad':
      return <Contabilidad investments={investments} enrichedMarkets={enrichedMarkets} presentationMode={presentationMode} userPlan={userPlan} />;
    case 'historialContable':
      return <HistorialContable userPlan={userPlan} presentationMode={presentationMode} />;
    case 'marketDatabase':
      return <MarketDatabase />;
    case 'financialDatabase':
      return <FinancialDatabase initialTicker={options?.ticker || ticker} />;
    case 'news':
      return <MarketNews portfolioTickers={portfolioTickers} onSelectTicker={onTickerSelect} />;
    case 'analyzer':
      return <AssetAnalyzer initialTicker={options?.ticker || ticker} onTickerSelect={onTickerSelect} />;
    case 'alerts':
      return <AlertsPanel isOpen={true} onClose={onClose} markets={enrichedMarkets} onTickerSelect={onTickerSelect} />;
    case 'universalComparator':
      return <UniversalComparator initialTicker={options?.ticker || ticker} />;
    case 'fullscreen':
      return <BigScreenMode 
        embedded={true} 
        ticker={ticker} 
        stockInfo={stockInfo} 
        stockHistory={stockHistory} 
        enrichedMarkets={enrichedMarkets || emptyArray} 
        priceChange={stockInfo?.day_change || 0}
        priceChangePercent={stockInfo?.day_change_percent || 0}
        onTickerSelect={onTickerSelect}
        onMinimize={onMinimize}
        onClose={onClose}
        presentationMode={presentationMode} 
      />;
    // ═══════════════════════════════════════════════════════════════════
    // HERRAMIENTAS FUSIONADAS (CENTROS)
    // ═══════════════════════════════════════════════════════════════════
    case 'heatmapCenter':
      return <HeatmapCenter onClose={onClose} investments={investments || {}} enrichedMarkets={enrichedMarkets || []} onTickerSelect={onTickerSelect} initialTab={options?.initialTab || 'correlation'} qualityTrend={!!options?.qualityTrend} />;
    case 'historicalCenter':
      return <HistoricalCenter onClose={onClose} />;
    case 'calculatorCenter':
      return <CalculatorCenter onClose={onClose} />;
    // ═══════════════════════════════════════════════════════════════════
    // OTROS COMPONENTES
    // ═══════════════════════════════════════════════════════════════════
    case 'candlestickChart':
      return <CandlestickChartWindow ticker={ticker} stockHistory={stockHistory} stockInfo={stockInfo} onTickerSelect={onTickerSelect} portfolioTickers={portfolioTickers} onClose={onClose} onMinimize={onMinimize} />;
    case 'backup':
      return <BackupManager />;
    case 'multiChart':
      return <MultiChartViewContent />;
    case 'manual':
      return <UserManual />;
    case 'stockScreener':
      return <StockScreenerPro onTickerSelect={onTickerSelect} />;
    case 'qualityTrend':
      return <QualityTrendScreener onTickerSelect={onTickerSelect} openWindow={openWindow} />;
    case 'proAnalyst':
      return <ProAnalyst onClose={onClose} openWindow={openWindow} initialTicker={options?.ticker || null} />;
    case 'asset360':
      return <Asset360Report initialTicker={options?.ticker || null} openWindow={openWindow} onClose={onClose} />;
    case 'gapHistory':
      return <GapHistory initialTicker={options?.ticker || null} openWindow={openWindow} />;
    case 'ebitdaAnalysis':
      return <EBITDAAnalysis onClose={onClose} isFullscreen={false} onToggleFullscreen={() => {}} openWindow={openWindow} initialTicker={options?.ticker} />;
    case 'financialGoals':
      return <FinancialGoals onClose={onClose} />;
    case 'watchlists':
      return <Watchlists onSelectTicker={onTickerSelect} />;
    case 'candlestickPatterns':
      return <CandlestickPatterns ticker={ticker} />;
    case 'sentimentComparator':
      return <SentimentComparator userPlan={userPlan} />;
    case 'emailAlerts':
      return <EmailAlerts />;
    case 'tradingJournal':
      return <TradingJournal />;
    case 'paperTrading':
      return <PaperTrading currentTicker={ticker} currentPrice={stockInfo?.current_price} />;
    default:
      return <div className="flex items-center justify-center h-full text-gray-400">Ventana no disponible</div>;
  }
};

// Hook para gestionar ventanas con persistencia
export const useWindowManager = () => {
  // Cargar estado guardado desde localStorage UNA SOLA VEZ
  const initialState = useMemo(() => {
    try {
      const saved = localStorage.getItem('windowManagerState');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      logger.warn('Error loading window state:', e);
    }
    return { openWindows: [], minimizedWindows: [], windowStates: {} };
  }, []);

  const [openWindows, setOpenWindows] = useState(() => {
    // Opción C: NO se restauran ventanas abiertas al cargar (dashboard limpio,
    // menor consumo de recursos). Solo se conserva `windowStates` (posición/
    // tamaño) y el acento por ventana para reabrir cada herramienta donde
    // quedó. Evita que ventanas grandes (p. ej. Analista Pro) tapen el dashboard.
    return [];
  });
  
  const [minimizedWindows, setMinimizedWindows] = useState(() => {
    // Opción C: tampoco se restauran ventanas minimizadas (no se pre-montan
    // componentes al arrancar → arranque más rápido).
    return [];
  });
  
  const [windowStates, setWindowStates] = useState(() => {
    return initialState.windowStates || {};
  });
  
  const [focusedWindow, setFocusedWindow] = useState(null);
  const [windowCounter, setWindowCounter] = useState(0);
  // Marca si el estado ha cambiado tras el mount inicial. Evita que `flushSave`
  // pise localStorage con un array vacío durante un unload prematuro (por
  // ejemplo, un reload que ocurre antes de que el usuario haga nada).
  const hasChangedRef = useRef(false);
  const mountedRef = useRef(false);
  // Snapshot de "mi espacio": solo se persiste tras una interacción REAL del
  // usuario (abrir/cerrar/minimizar), nunca en el guardado automático de carga.
  // Así el StrictMode del dev-preview no borra la sesión anterior con un [].
  const userInteractedRef = useRef(false);
  useEffect(() => {
    // El primer render NO cuenta como "cambio" (es el mount).
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    hasChangedRef.current = true;
  }, [openWindows, minimizedWindows, windowStates]);

  // Persistir el SNAPSHOT de sesión ("mi espacio") solo tras interacción real.
  // Guarda ventanas abiertas con su posición/tamaño resueltos.
  useEffect(() => {
    if (!userInteractedRef.current) return;
    try {
      const snap = openWindows.map(w => ({
        windowId: w.windowId,
        position: windowStates[w.windowId]?.position || w.position,
        size: windowStates[w.windowId]?.size || w.size
      }));
      localStorage.setItem('kap_window_session', JSON.stringify(snap));
    } catch { /* noop */ }
  }, [openWindows, windowStates]);

  // Guardar estado en localStorage
  const saveState = useCallback(() => {
    try {
      // Combinar posiciones actuales de openWindows con windowStates
      const mergedStates = { ...windowStates };
      openWindows.forEach(w => {
        if (!mergedStates[w.windowId]) {
          mergedStates[w.windowId] = {};
        }
        // Usar posición/tamaño de openWindows como fallback si no hay en windowStates
        if (!mergedStates[w.windowId].position) {
          mergedStates[w.windowId].position = w.position;
        }
        if (!mergedStates[w.windowId].size) {
          mergedStates[w.windowId].size = w.size;
        }
      });
      
      const stateToSave = {
        openWindows: openWindows.map(w => ({
          windowId: w.windowId,
          position: mergedStates[w.windowId]?.position || w.position,
          size: mergedStates[w.windowId]?.size || w.size
        })),
        minimizedWindows,
        windowStates: mergedStates
      };
      localStorage.setItem('windowManagerState', JSON.stringify(stateToSave));
    } catch (e) {
      logger.warn('Error saving window state:', e);
    }
  }, [openWindows, minimizedWindows, windowStates]);

  // Guardar cuando cambia el estado (con debounce). También respeta hasChangedRef
  // para no sobrescribir localStorage durante el mount inicial si el usuario
  // aún no ha hecho ningún cambio.
  useEffect(() => {
    if (!hasChangedRef.current) return;
    const timer = setTimeout(saveState, 500);
    return () => clearTimeout(timer);
  }, [saveState]);

  // 📱 PWA: guardar INMEDIATAMENTE cuando la app pasa a segundo plano.
  // Android puede matar el proceso antes de que el debounce de 500ms se ejecute,
  // así que forzamos un save síncrono en visibilitychange→hidden y pagehide.
  // Importante: solo guarda si el usuario ha modificado el estado (hasChangedRef),
  // así no pisamos las ventanas persistidas cuando la app se cierra sin abrir nada.
  useEffect(() => {
    const flushSave = () => {
      if (!hasChangedRef.current) return;
      try { saveState(); } catch { /* ignore */ }
    };
    const handleHidden = () => {
      if (document.visibilityState === 'hidden') flushSave();
    };
    document.addEventListener('visibilitychange', handleHidden);
    window.addEventListener('pagehide', flushSave);
    window.addEventListener('beforeunload', flushSave);
    return () => {
      document.removeEventListener('visibilitychange', handleHidden);
      window.removeEventListener('pagehide', flushSave);
      window.removeEventListener('beforeunload', flushSave);
    };
  }, [saveState]);

  // Callback para actualizar estado de ventana (posición, tamaño)
  const updateWindowState = useCallback((windowId, state) => {
    setWindowStates(prev => ({
      ...prev,
      [windowId]: { ...prev[windowId], ...state }
    }));
  }, []);

  // Calcular posición en cascada
  const getNextPosition = useCallback(() => {
    const baseX = 180;
    const baseY = 50;
    const offsetX = 40;
    const offsetY = 35;
    const index = windowCounter % 8;
    return {
      x: baseX + (index * offsetX),
      y: baseY + (index * offsetY)
    };
  }, [windowCounter]);

  // Abrir una nueva ventana
  const openWindow = useCallback((windowId, options = {}) => {
    userInteractedRef.current = true;
    const windowConfig = WINDOWS_CONFIG.find(w => w.id === windowId);
    if (!windowConfig) {
      logger.warn(`WindowManager: Config not found for windowId: ${windowId}`);
      return;
    }

    logger.log(`WindowManager: Opening window ${windowId}, current open: ${openWindows.map(w => w.windowId).join(', ')}`);

    // Si ya está abierta (incluso minimizada), solo enfocarla/restaurarla
    const existingOpen = openWindows.find(w => w.windowId === windowId);
    if (existingOpen) {
      logger.log(`WindowManager: Window ${windowId} already open, restoring/focusing`);
      if (minimizedWindows.includes(existingOpen.windowId)) {
        setMinimizedWindows(prev => prev.filter(id => id !== existingOpen.windowId));
      }
      // Actualizar opciones si se proporcionan
      if (options && Object.keys(options).length > 0) {
        setOpenWindows(prev => prev.map(w => 
          w.windowId === windowId ? { ...w, options } : w
        ));
        // Forzar remontaje del contenido para que aplique las nuevas opciones
        // (p.ej. ticker de deep-link cuando la ventana ya estaba abierta/persistida)
        window.dispatchEvent(new CustomEvent('kap-window-refresh', { detail: { windowId } }));
      }
      setFocusedWindow(existingOpen.windowId);
      return;
    }

    // Usar posición/tamaño guardados si existen
    const savedState = windowStates[windowId];
    const position = savedState?.position || (windowConfig.isFullscreen ? { x: 100, y: 30 } : getNextPosition());
    const size = savedState?.size || windowConfig.defaultSize;
    
    // IMPORTANTE: Usar función de actualización para evitar condiciones de carrera
    setOpenWindows(prev => {
      logger.log(`WindowManager: Adding ${windowId} to existing ${prev.length} windows`);
      return [...prev, {
        windowId,
        config: windowConfig,
        position,
        size,
        options // Guardar opciones adicionales
      }];
    });
    
    setFocusedWindow(windowId);
    setWindowCounter(prev => prev + 1);
  }, [openWindows, minimizedWindows, windowStates, getNextPosition]);

  // Cerrar una ventana
  const closeWindow = useCallback((windowIdOrObject) => {
    userInteractedRef.current = true;
    // Soportar tanto windowId (string) como objeto de ventana
    const windowId = typeof windowIdOrObject === 'string' 
      ? windowIdOrObject 
      : windowIdOrObject?.windowId;
    
    if (!windowId) {
      logger.warn('WindowManager: closeWindow called with invalid argument:', windowIdOrObject);
      return;
    }
    
    logger.log(`WindowManager: Closing window ${windowId}`);
    
    // Si el navegador está en modo fullscreen, salir de fullscreen al cerrar la ventana
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        logger.warn('Error exiting fullscreen on window close:', err);
      });
    }
    
    setOpenWindows(prev => {
      const newWindows = prev.filter(w => w.windowId !== windowId);
      logger.log(`WindowManager: After close, remaining windows: ${newWindows.map(w => w.windowId).join(', ') || 'none'}`);
      return newWindows;
    });
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    if (focusedWindow === windowId) {
      setFocusedWindow(null);
    }
  }, [focusedWindow]);

  // Minimizar una ventana
  const minimizeWindow = useCallback((windowId) => {
    userInteractedRef.current = true;
    logger.log(`WindowManager: Minimizing window ${windowId}`);
    if (!minimizedWindows.includes(windowId)) {
      setMinimizedWindows(prev => [...prev, windowId]);
    }
    if (focusedWindow === windowId) {
      setFocusedWindow(null);
    }
  }, [minimizedWindows, focusedWindow]);

  // Restaurar una ventana minimizada
  const restoreWindow = useCallback((windowId) => {
    logger.log(`WindowManager: Restoring window ${windowId}`);
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    setFocusedWindow(windowId);
  }, []);

  // Enfocar una ventana
  const focusWindow = useCallback((windowId) => {
    if (minimizedWindows.includes(windowId)) {
      setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    }
    setFocusedWindow(windowId);
  }, [minimizedWindows]);

  // Cerrar todas las ventanas
  const closeAllWindows = useCallback(() => {
    userInteractedRef.current = true;
    // Salir de fullscreen si está activo
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        logger.warn('Error exiting fullscreen on closeAll:', err);
      });
    }
    setOpenWindows([]);
    setMinimizedWindows([]);
    setFocusedWindow(null);
  }, []);

  // Minimizar todas las ventanas abiertas
  const minimizeAllWindows = useCallback(() => {
    const windowIds = openWindows.map(w => w.windowId);
    setMinimizedWindows(windowIds);
    setFocusedWindow(null);
  }, [openWindows]);

  // Restaurar todas las ventanas minimizadas
  const restoreAllWindows = useCallback(() => {
    setMinimizedWindows([]);
    if (openWindows.length > 0) {
      setFocusedWindow(openWindows[openWindows.length - 1].windowId);
    }
  }, [openWindows]);

  // Obtener ventanas minimizadas con su info completa
  const getMinimizedWindowsInfo = useCallback(() => {
    return openWindows
      .filter(w => minimizedWindows.includes(w.windowId))
      .map(w => {
        // Acento real por ventana (elegido por el usuario), para el punto de
        // color en la barra de tareas. Fallback al color del config.
        let accentColor = w.config.color;
        try {
          const a = localStorage.getItem(`kap_win_accent_${w.windowId}`);
          if (a && WIN_ACCENT_HEX[a]) accentColor = WIN_ACCENT_HEX[a];
        } catch { /* noop */ }
        return {
          windowId: w.windowId,
          toolId: w.windowId,
          accentColor,
          tool: {
            id: w.config.id,
            name: w.config.name,
            icon: w.config.icon,
            color: w.config.color
          }
        };
      });
  }, [openWindows, minimizedWindows]);

  // Aplicar un layout predefinido
  const applyLayout = useCallback((layout) => {
    // Cerrar todas las ventanas actuales
    setOpenWindows([]);
    setMinimizedWindows([]);
    setFocusedWindow(null);
    
    // Actualizar windowStates con las posiciones del layout
    if (layout.positions) {
      setWindowStates(layout.positions);
    }
    
    // Abrir las ventanas del layout con un pequeño delay para animación
    setTimeout(() => {
      layout.windows.forEach((windowId, index) => {
        const windowConfig = WINDOWS_CONFIG.find(w => w.id === windowId);
        if (!windowConfig) return;
        
        const savedPosition = layout.positions?.[windowId];
        const position = savedPosition ? { x: savedPosition.x, y: savedPosition.y } : { x: 180 + (index * 40), y: 50 + (index * 35) };
        const size = savedPosition ? { width: savedPosition.width, height: savedPosition.height } : windowConfig.defaultSize;
        
        setOpenWindows(prev => [...prev, {
          windowId,
          config: windowConfig,
          position,
          size
        }]);
      });
      
      // Enfocar la última ventana
      if (layout.windows.length > 0) {
        setFocusedWindow(layout.windows[layout.windows.length - 1]);
      }
    }, 100);
  }, []);

  // Nº de ventanas guardadas de la última sesión (para habilitar el botón "Restaurar mi Espacio")
  const getSavedSessionCount = useCallback(() => {
    try {
      const snap = JSON.parse(localStorage.getItem('kap_window_session'));
      return (snap || []).filter(sw => WINDOWS_CONFIG.find(c => c.id === sw.windowId)).length;
    } catch { return 0; }
  }, []);

  // Reabrir de golpe todas las ventanas de la última sesión en su posición/tamaño guardados.
  // (Con la opción C el arranque es limpio; esto las trae de vuelta a demanda.)
  const restoreLastSession = useCallback(() => {
    userInteractedRef.current = true;
    let snap = null;
    try { snap = JSON.parse(localStorage.getItem('kap_window_session')); } catch { snap = null; }
    const savedOpen = (snap || []).filter(sw => WINDOWS_CONFIG.find(c => c.id === sw.windowId));
    if (savedOpen.length === 0) return 0;
    const restored = savedOpen.map(sw => {
      const config = WINDOWS_CONFIG.find(c => c.id === sw.windowId);
      return {
        windowId: sw.windowId,
        config,
        position: sw.position || { x: 180, y: 50 },
        size: sw.size || config.defaultSize,
      };
    });
    setOpenWindows(prev => {
      const existing = new Set(prev.map(w => w.windowId));
      return [...prev, ...restored.filter(w => !existing.has(w.windowId))];
    });
    setMinimizedWindows([]);
    setFocusedWindow(restored[restored.length - 1].windowId);
    return restored.length;
  }, []);

  return {
    openWindows,
    minimizedWindows,
    focusedWindow,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    focusWindow,
    closeAllWindows,
    minimizeAllWindows,
    restoreAllWindows,
    getMinimizedWindowsInfo,
    updateWindowState,
    applyLayout,
    restoreLastSession,
    getSavedSessionCount,
    isWindowOpen: (windowId) => openWindows.some(w => w.windowId === windowId),
    isWindowMinimized: (windowId) => minimizedWindows.includes(windowId)
  };
};

// Componente que renderiza todas las ventanas abiertas
const WindowManager = ({ 
  openWindows, 
  minimizedWindows = [],
  focusedWindow, 
  onClose, 
  onFocus,
  onMinimize,
  onStateChange,
  openWindow,  // Nueva prop para abrir ventanas desde componentes hijos
  ticker,
  stockHistory,
  stockInfo,
  onTickerSelect,
  portfolioTickers,
  enrichedMarkets = []
}) => {
  // Estado para forzar actualización de contenido de ventanas
  const [refreshKeys, setRefreshKeys] = useState({});
  
  // Función para actualizar una ventana específica
  const handleRefreshWindow = useCallback((windowId) => {
    setRefreshKeys(prev => ({
      ...prev,
      [windowId]: (prev[windowId] || 0) + 1
    }));
  }, []);

  // Remontaje forzado cuando openWindow (hook) pide refrescar una ventana ya abierta
  useEffect(() => {
    const onRefresh = (e) => {
      const windowId = e?.detail?.windowId;
      if (windowId) handleRefreshWindow(windowId);
    };
    window.addEventListener('kap-window-refresh', onRefresh);
    return () => window.removeEventListener('kap-window-refresh', onRefresh);
  }, [handleRefreshWindow]);

  // Calcular z-index basado en el orden de foco
  // Las ventanas más recientes tienen z-index más alto
  const getZIndex = (windowId) => {
    const baseZ = 100;
    // La ventana enfocada siempre va arriba
    if (windowId === focusedWindow) return baseZ + 1000;
    // Las demás ventanas se ordenan por su índice en el array (las últimas añadidas tienen índice mayor)
    const index = openWindows.findIndex(w => w.windowId === windowId);
    return baseZ + (index * 10);
  };

  // feb 2026: Cartera Detallada ya no se renderiza como portal fullscreen aparte.
  // Ahora es una FloatingWindow normal (redimensionable/arrastrable) como el resto.

  return (
    <>
      {/* Ventanas flotantes normales (incluye 'fullscreen' / Cartera Detallada) */}
      {openWindows.map((window) => (
        <FloatingWindow
          key={window.windowId}
          id={window.windowId}
          title={window.config.fullName}
          icon={window.config.icon}
          color={window.config.color}
          initialPosition={window.position}
          initialSize={window.size}
          zIndex={getZIndex(window.windowId)}
          isMinimized={minimizedWindows.includes(window.windowId)}
          onClose={onClose}
          onFocus={onFocus}
          onMinimize={onMinimize}
          onStateChange={onStateChange}
          onRefresh={() => handleRefreshWindow(window.windowId)}
        >
          <Suspense fallback={<LoadingSpinner color={window.config.color} />}>
            <WindowContent 
              key={`${window.windowId}-${refreshKeys[window.windowId] || 0}`}
              windowId={window.windowId}
              ticker={ticker}
              stockHistory={stockHistory}
              stockInfo={stockInfo}
              onTickerSelect={onTickerSelect}
              portfolioTickers={portfolioTickers}
              enrichedMarkets={enrichedMarkets}
              options={window.options}
              onClose={() => onClose(window.windowId)}
              onMinimize={() => onMinimize(window.windowId)}
              openWindow={openWindow}
              onFocus={onFocus}
            />
          </Suspense>
        </FloatingWindow>
      ))}
    </>
  );
};

export default WindowManager;
